import { USER } from "./user";

/**
 * One door to localStorage (fix docket CL1). Every read and write is guarded —
 * Safari private mode and full quotas throw, and a lost preference must never
 * take the tap that wrote it down with it (the search-recents bug, RF5).
 *
 * Dismissals share one encoding — the expiry SECOND under the key — where the
 * old per-component copies had three: seconds-until-expiry, ms-at-dismissal,
 * and an unguarded variant. `scopedKey` appends the signed-in member's id so a
 * dismissal on a shared device follows the account, not the browser.
 */
export function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function storageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — the preference just will not survive */
  }
}

export function storageRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** The key, owned by the current account when one is signed in. */
export function scopedKey(base: string): string {
  const id = USER?.informations?.id;
  return id ? `${base}:${id}` : base;
}

/** True while `key` holds an expiry second still in the future; stale keys are
    swept on read. */
export function isDismissed(key: string): boolean {
  const raw = storageGet(key);
  if (!raw) return false;
  const until = Number(raw);
  if (!Number.isFinite(until) || until <= Date.now() / 1000) {
    storageRemove(key);
    return false;
  }
  return true;
}

/** Dismiss `key` for `seconds` from now. */
export function dismiss(key: string, seconds: number): void {
  storageSet(key, String(Math.floor(Date.now() / 1000) + seconds));
}
